import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { companyService } from "@/features/company/services/companyService";

interface CompanyOption {
  value: string;
  label: string;
}

interface CompanySearchSelectProps {
  value: string;
  onChange: (companyId: string, label: string) => void;
  label?: string;
  required?: boolean;
  hint?: string;
  feature?: "ticketing" | "flight_tracker" | "company_form";
}

const MAX_RESULTS = 15;

export const CompanySearchSelect = ({
  value,
  onChange,
  label,
  required,
  hint,
  feature,
}: CompanySearchSelectProps) => {
  const { t } = useTranslation();
  const [allCompanies, setAllCompanies] = useState<CompanyOption[]>([]);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    companyService
      .getDropdown(feature)
      .then((data) => setAllCompanies(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [feature]);

  // Set display value when value changes from outside
  useEffect(() => {
    if (value) {
      const match = allCompanies.find((c) => c.value === value);
      if (match) setSearch(match.label);
    } else {
      setSearch("");
    }
  }, [value, allCompanies]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Reset search to selected label if user clicked away without selecting
        if (value) {
          const match = allCompanies.find((c) => c.value === value);
          if (match) setSearch(match.label);
        } else {
          setSearch("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value, allCompanies]);

  const filtered = useCallback(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allCompanies.slice(0, MAX_RESULTS);
    return allCompanies
      .filter((c) => c.label.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS);
  }, [search, allCompanies]);

  const results = filtered();

  const handleSelect = (company: CompanyOption) => {
    setSearch(company.label);
    setIsOpen(false);
    onChange(company.value, company.label);
  };

  const handleClear = () => {
    setSearch("");
    onChange("", "");
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
            if (value) onChange("", "");
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={loading ? t("leave.company_loading") : t("leave.company_search_placeholder")}
          disabled={loading}
          className="w-full pl-9 pr-9 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
        />

        {(search || value) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}

      {/* Dropdown */}
      {isOpen && !loading && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              {t("leave.company_no_results")}
            </div>
          ) : (
            <>
              {results.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => handleSelect(c)}
                  className={`w-full text-left px-3 py-2.5 md:py-2 text-sm hover:bg-indigo-50 transition-colors ${
                    c.value === value
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {c.label}
                </button>
              ))}
              {allCompanies.length > MAX_RESULTS && search.trim() === "" && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
                  {t("leave.company_type_to_filter")}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
